import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import { useFloatingPortal } from '@/libraries/contexts/floating-portal-context';
import styles from './floating-tooltip.module.scss';

export type FloatingTooltipPlacement = 'bottom' | 'left' | 'right' | 'top';

const arrowPadding = 20;

export interface FloatingTooltipProps {
  /** The trigger element */
  children?: React.ReactNode;
  /** The class name to apply to the trigger wrapper */
  className?: string;
  /** Optional tooltip content — overrides headline and text */
  content?: React.ReactNode;
  /** The headline of the tooltip */
  headline?: string;
  /** Optional maximum width for the tooltip popup */
  maxWidth?: number;
  /** The preferred placement of the tooltip */
  placement?: FloatingTooltipPlacement;
  /** Optional portal element to render the tooltip into (useful for modals) */
  portalElement?: HTMLElement | null;
  /** Rich-text HTML string to display in the tooltip body */
  text?: string;
  /** Applies a minimum width to the tooltip popup */
  useMinimumWidth?: boolean;
}

const FloatingTooltip = ({
  children,
  className,
  content,
  headline,
  maxWidth,
  placement,
  portalElement,
  text,
  useMinimumWidth = true,
}: FloatingTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);
  const getContextPortalElement = useFloatingPortal();

  const effectivePortalElement =
    getContextPortalElement?.() ?? portalElement ?? undefined;

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      flip(),
      shift({ padding: arrowPadding }),
      arrow({ element: arrowRef, padding: arrowPadding }),
    ],
    onOpenChange: setIsOpen,
    open: isOpen,
    placement,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: {
      close: 100,
      open: 100,
    },
    move: false,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <React.Fragment>
      <div
        ref={refs.setReference}
        className={classNames(styles.component, className)}
        {...getReferenceProps()}
      >
        {children}
      </div>

      {isOpen && (
        <FloatingPortal root={effectivePortalElement}>
          <div
            ref={refs.setFloating}
            className={classNames(styles.popup, {
              [styles.hasMinimumWidth]: useMinimumWidth,
            })}
            role="tooltip"
            style={{
              ...floatingStyles,
              ...(maxWidth && { maxWidth: `${maxWidth}px` }),
            }}
            {...getFloatingProps()}
          >
            {content ?? (
              <React.Fragment>
                {headline && (
                  <Typography tagStyle="bodySmallBold">{headline}</Typography>
                )}

                {text && <RichText text={text} typographyVariant="bodySmall" />}
              </React.Fragment>
            )}

            <FloatingArrow
              ref={arrowRef}
              className={styles.arrow}
              context={context}
              strokeWidth={1}
            />
          </div>
        </FloatingPortal>
      )}
    </React.Fragment>
  );
};

FloatingTooltip.displayName = 'FloatingTooltip';

export default FloatingTooltip;
